<template>
    <aside class="analytics-header" v-if="$store.state.user_data && can_start" :key="key_ref">
        <div class="header">
            <div class="title">
                <h2>Analytics</h2>
                <queue-progress :isStatic="true" />
            </div>
            <!-- Creator Tag Filter -->
            <div class="creator-tag-dropdown" v-if="can_show_creator_tags" ref="tagDropdown">
                <Button 
                    size="40" 
                    :color="show_tag_menu ? 'black-60' : 'black-20'" 
                    :style="'border-square'" 
                    @click.native.stop="toggle_tag_dropdown"
                    class="tag-button"
                    :class="{ 'active': show_tag_menu }"
                >
                    <SvgIcon name="tag" class="icon-left" />
                    <span>{{ creator_tag_button_text }}</span>
                    <SvgIcon name="arrow" class="icon-right" :class="{ 'rotate': show_tag_menu }" />
                </Button>
                
                <!-- Dropdown Menu -->
                <div v-if="show_tag_menu" class="tag-dropdown-menu" @click.stop>
                    <!-- Operation Type Selection -->
                    <div class="operation-type-section">
                        <label class="radio-item">
                            <input type="radio" v-model="temp_operation_type" value="or" />
                            <span>Or</span>
                        </label>
                        <label class="radio-item">
                            <input type="radio" v-model="temp_operation_type" value="and" />
                            <span>And</span>
                        </label>
                    </div>
                    <!-- Divider Line -->
                    <div class="divider-line"></div>
                    <Scrollbar class="tag-list" :show_bar="true">
                        <div 
                            v-for="tag in creator_tags" 
                            :key="tag.id" 
                            class="tag-item"
                            :class="{ 'disabled': tag.id === 0 && temp_operation_type === 'and' }"
                            @click="toggle_tag(tag)"
                        >
                            <input 
                                type="checkbox" 
                                :checked="tag.is_selected" 
                                :disabled="tag.id === 0 && temp_operation_type === 'and'"
                                @click.stop
                                @change="toggle_tag(tag)"
                            />
                            <span>{{ tag.text }}</span>
                        </div>
                    </Scrollbar>
                    <!-- Footer buttons -->
                    <div class="footer">
                        <Button type="button" color="white" :style="'border'" size="32" @click.native="clear_tags" class="clear-btn">Clear</Button>
                        <Button type="button" color="dark-blue" size="32" @click.native="apply_tags" class="apply-btn">See results</Button>
                    </div>
                </div>
            </div>
            <!-- Date Picker moved to right side -->
            <div class="right-controls">
                <Checkbox v-if="can_show_checkbox && show_check_box" size="small" :key="'all'" :label_text="'Include Shortlink Data'" :checked="sortlink_data"
                    @click="sortLinkClick" 
                    :desc="`This only appears on products that actually have shortlink data associated.  If someone unchecks the box - this should go away and the numbers adjust accordingly. `"
                    :had_svg="true"/>
                <DatePicker class="btn" :date_range="date_range" @update-date="updateDate" @close="isClose"
                    :is_post_pick="need_han_date ? true : false" :cus_text="cus_text"/>
            </div>
        </div>
        <!-- Horizontal line below title -->
        <div class="header-divider"></div>
        <!-- Navigation row with tabs and update time -->
        <div class="nav-row">
            <Navi :menu="menu" />
            <div class="update-time" v-if="!can_show_checkbox || !show_check_box">Last Update: {{$store.state.user_data.merchant_data_update_hour_ago}}h ago</div>
        </div>
    </aside>
</template>

<script>
import SvgIcon from '@/components/ui/SvgIcon.vue';
import Navi from '@/components/Navi.vue';
import Button from '@/components/ui/Button.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import moment from 'moment';
import { handleMaxMinFun } from '@/utils/SwitchFun.js';
import Checkbox from '@/components/ui/Checkbox.vue';
import { getTags } from '@/utils/CreatorTagFun.js';

export default {
    name: 'AnalyticsHeader',
    emits: ['update_date',],

    components: {
        SvgIcon,
        Navi,
        Button,
        DatePicker,
        Checkbox,
        Scrollbar
    },

    props:{
        data_statistic:{
            type: Object,
            default: null
        },
        show_check_box:{
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            menu: [],
            key_ref: 0,
            date_range: {
                start_date: moment().add(-30, 'days').toDate(),
                end_date: moment().add(-1, 'days').toDate()
            },
            cus_text:'',
            can_start:false,//阻止有些元件在時間整理完之前出現
            sortlink_data:0,
            creator_tags: [],
            selected_tags: [],
            temp_selected_tags: [], // Temporary selection before applying
            tag_filter_key: 0,
            show_tag_menu: false,
            is_loading_tags: false,
            tag_operation_type: 'or', // Current operation type
            temp_operation_type: 'or', // Temporary operation type before applying
        }
    },

    computed: {
        need_han_date() {
            return this.$store.state.user_data.contract_status && this.$route.fullPath === '/analytics/content'
        },
        handle_contract_start() {
            return this.need_han_date ? moment(this.$store.state.user_data.contract_start_time).format('YYYY-MM-DD') : this.date_range.start_date;
        },
        selected_tag_ids() {
            // Return array of selected tag IDs for dropdown default value
            return this.selected_tags.map(tag => tag.id);
        },
        creator_tag_button_text() {
            // Dynamic button text based on selection
            if (this.selected_tags.length === 0) {
                return 'Creator Tag: All';
            }
            return `Creator Tag: Selected(${this.selected_tags.length})`;
        },
        can_show_creator_tags() {
            // Follow existing pattern: can_start + data conditions + loading state
            return this.can_start && !this.is_loading_tags && this.creator_tags.length > 1;
        },
        plusData(){
            let da=this.data_statistic;
            let can_toggle_sortlink= da ? da.short_link_click_sum + da.short_link_sales_sum + da.short_link_sales_value_sum : 0;
            if(!da){
                return{
                    can_toggle_sortlink:can_toggle_sortlink
                }
            }if(this.sortlink_data){
                return{
                    can_toggle_sortlink:can_toggle_sortlink
                }
                
            }else{
                return{
                    can_toggle_sortlink:can_toggle_sortlink
                }
            }
        },
        //判斷能不能把sortlink展現出來，如果不能，last_time也不能
        can_show_checkbox(){
            return this.can_start &&this.data_statistic && this.plusData.can_toggle_sortlink;
        },
    },

    watch:{
        $route: {
            immediate: true,
            handler(to) {
                if(to.fullPath=== '/analytics/content'){
                    this.date_range.start_date = this.need_han_date ? moment(this.$store.state.user_data.contract_start_time).format('YYYY-MM-DD') : this.date_range.start_date;
                    this.date_range.end_date = this.need_han_date ? moment().add(-1, 'days').toDate(): this.date_range.end_date;
                }
            },
        },
        temp_operation_type(newVal) {
            // When switching to "and", deselect "All Tags" if it's selected
            if (newVal === 'and') {
                const allTagsIndex = this.creator_tags.findIndex(t => t.id === 0);
                if (allTagsIndex !== -1 && this.creator_tags[allTagsIndex].is_selected) {
                    this.creator_tags[allTagsIndex].is_selected = false;
                    // If "All Tags" was the only selection, we need to clear temp_selected_tags
                    if (this.temp_selected_tags.length === 0) {
                        // No specific tags selected, just "All Tags" was selected
                    }
                }
            }
        },
    },

    methods: {
        async load_creator_tags() {
            // Follow same pattern as sortlink_data - check if tags are already loaded this session
            if (this.$store.state.save_tags_loaded && this.$store.state.save_analytics_tags.length > 0) {
                // Use cached tags from store - following existing pattern
                this.creator_tags = [...this.$store.state.save_analytics_tags];
                this.selected_tags = this.$store.state.save_selected_tags || [];
                
                // Initialize temp selection
                this.temp_selected_tags = [...this.selected_tags];
                
                // Force dropdown to update
                this.tag_filter_key++;
                return;
            }

            // Set loading state
            this.is_loading_tags = true;

            try {
                const tagsData = await getTags(this);
                if (tagsData && tagsData.data && tagsData.data.length > 0) {
                    // Only create filter if we have actual creator tags
                    this.creator_tags = [
                        { id: 0, text: 'All Tags', is_selected: true },
                        ...tagsData.data.map(tag => ({ ...tag, is_selected: false }))
                    ];
                    
                    // Initialize empty selections
                    this.selected_tags = [];
                    this.temp_selected_tags = [];
                    
                    // Store tags in state following existing pattern (direct assignment, not mutations)
                    this.$store.state.save_analytics_tags = this.creator_tags;
                    this.$store.state.save_tags_loaded = true;
                } else {
                    // No creator tags available - keep empty array
                    this.creator_tags = [];
                    console.log('No creator tags available for this merchant');
                }
            } catch (error) {
                console.error('Failed to load creator tags:', error);
                this.creator_tags = [];
            } finally {
                // Always clear loading state
                this.is_loading_tags = false;
            }
        },

        toggle_tag_dropdown() {
            this.show_tag_menu = !this.show_tag_menu;
            if (this.show_tag_menu) {
                // Initialize temp selection with current selection
                this.temp_selected_tags = [...this.selected_tags];
                this.temp_operation_type = this.tag_operation_type; // Restore current operation type
                // Add click outside listener
                setTimeout(() => {
                    document.addEventListener('click', this.handle_click_outside);
                }, 100);
            } else {
                document.removeEventListener('click', this.handle_click_outside);
            }
        },

        handle_click_outside(event) {
            if (this.$refs.tagDropdown && !this.$refs.tagDropdown.contains(event.target)) {
                this.show_tag_menu = false;
                document.removeEventListener('click', this.handle_click_outside);
            }
        },

        toggle_tag(tag) {
            // Prevent clicking on "All Tags" when operation type is "and"
            if (tag.id === 0 && this.temp_operation_type === 'and') {
                return;
            }
            
            if (tag.id === 0) {
                // If "All Tags" is selected, clear all other selections and set operation type to "or"
                this.creator_tags = this.creator_tags.map(t => ({
                    ...t,
                    is_selected: t.id === 0
                }));
                this.temp_selected_tags = [];
                this.temp_operation_type = 'or'; // Automatically set to "or" when "All Tags" is selected
            } else {
                // Toggle the selected tag
                const tagIndex = this.creator_tags.findIndex(t => t.id === tag.id);
                if (tagIndex !== -1) {
                    this.creator_tags[tagIndex].is_selected = !this.creator_tags[tagIndex].is_selected;
                    
                    // Update temp selection
                    const tempIndex = this.temp_selected_tags.findIndex(t => t.id === tag.id);
                    if (tempIndex !== -1) {
                        this.temp_selected_tags.splice(tempIndex, 1);
                    } else {
                        this.temp_selected_tags.push(tag);
                    }
                    
                    // If any tag is selected, unselect "All Tags"
                    if (this.temp_selected_tags.length > 0) {
                        const allTagsIndex = this.creator_tags.findIndex(t => t.id === 0);
                        if (allTagsIndex !== -1) {
                            this.creator_tags[allTagsIndex].is_selected = false;
                        }
                    } else {
                        // If no tags selected, select "All Tags" and set to "or"
                        const allTagsIndex = this.creator_tags.findIndex(t => t.id === 0);
                        if (allTagsIndex !== -1) {
                            this.creator_tags[allTagsIndex].is_selected = true;
                            this.temp_operation_type = 'or';
                        }
                    }
                }
            }
        },

        clear_tags() {
            // Clear all selections and select "All Tags"
            this.creator_tags = this.creator_tags.map(tag => ({
                ...tag,
                is_selected: tag.id === 0
            }));
            this.temp_selected_tags = [];
            this.temp_operation_type = 'or'; // Reset to "or" when clearing
        },

        apply_tags() {
            // Apply the current selection
            this.selected_tags = [...this.temp_selected_tags];
            this.tag_operation_type = this.temp_operation_type;
            
            // Store selected tags in state following existing pattern
            this.$store.state.save_selected_tags = this.selected_tags;
            this.$store.state.save_tag_operation_type = this.tag_operation_type;

            // Update the stored tags with selection state
            this.$store.state.save_analytics_tags = [...this.creator_tags];
            
            // Emit event for other components to react to tag changes
            // Include operation type in the event data
            this.EventBus.emit('analytics-tags-updated', {
                tags: this.selected_tags,
                operation_type: this.tag_operation_type
            });
            
            // Close the dropdown
            this.show_tag_menu = false;
            document.removeEventListener('click', this.handle_click_outside);
        },

        sortLinkClick(){
            this.sortlink_data=this.sortlink_data?  0 : 1;
            this.$emit('handleSortLink', this.sortlink_data );
        },

        downloadPdf() {
            this.$notify({
                title: 'Coming soon',
                type: 'warn',
                duration: 3000,
                group: 'center',
            });
        },
        updateDate(obj) {
            this.date_range.start_date = obj.start_date;
            this.date_range.end_date = obj.end_date;
            this.cus_text=obj.cus;

            // Save dates to store for persistence across tab switches
            this.$store.state.save_analytics_start_date = obj.start_date;
            this.$store.state.save_analytics_end_date = obj.end_date;

            this.EventBus.emit('update-date-at-analytics', obj);
            this.EventBus.emit('update-date', obj);

        },

        isClose() {
            setTimeout(() => {
                this.key_ref += 1;
            }, 100)
        },

        startFunction(){
            // Check if we have saved dates in store first
            if (this.$store.state.save_analytics_start_date && this.$store.state.save_analytics_end_date) {
                this.date_range.start_date = this.$store.state.save_analytics_start_date;
                this.date_range.end_date = this.$store.state.save_analytics_end_date;
            }

            let checkTime = handleMaxMinFun( this.date_range.start_date , this.date_range.end_date ,this);
            this.date_range.start_date = checkTime[0];
            this.date_range.end_date = checkTime[1];
            this.sortlink_data = this.init_sortlink!==undefined ? this.init_sortlink : this.$store.state.save_sortlink_data;//放入預設
            this.can_start=true;
            this.menu=[
                {
                    text: 'Overview',
                    to: '/analytics/overview',
                },
                {
                    text: 'Products',
                    to: '/analytics/products',
                },
                //Collections
                {
                    text: 'Collections',
                    to: '/analytics/collections',
                },
                {
                    text: 'Posts',
                    to: '/analytics/posts',
                },
                {
                    text: 'Branded Content',
                    to: '/analytics/content',
                    // for_in:true,
                },
            ]
        },  
    },

    created() {
        // Reset tags cache on page refresh (F5) - following existing pattern
        // This ensures fresh data when user manually refreshes
        window.addEventListener('beforeunload', () => {
            this.$store.state.save_tags_loaded = false;
        });
    },

    beforeUnmount() {
        // Clean up event listeners
        document.removeEventListener('click', this.handle_click_outside);
    },

    mounted(){
        this.startFunction();
        
        // Initialize selected tags from store if available
        if (this.$store.state.save_selected_tags && this.$store.state.save_selected_tags.length > 0) {
            this.selected_tags = this.$store.state.save_selected_tags;
        }
        
        // Load creator tags - they will be cached in store for tab switches
        this.load_creator_tags();
        // if(this.$route.fullPath === '/analytics/content'){
        //     this.date_range.start_date = this.need_han_date ? moment(this.$store.state.user_data.contract_start_time).format('YYYY-MM-DD') : this.date_range.start_date;
        //     this.date_range.end_date = this.need_han_date ? moment().add(-1, 'days').toDate() : this.date_range.end_date;
        // }
    }
}
</script>
<style lang="scss">
.analytics-header {
    width: 100%;
    overflow: visible;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 20;
    padding-bottom: 16px;

    .header {
        width: 100%;
        overflow: visible;
        display: flex;
        align-items: center;
        padding-bottom: 16px;

        .title {
            display: flex;
            align-items: center;
            margin-right: 16px;

            h2 {
                font: var(--font-demi-36);
                color: black;
            }
        }

        .creator-tag-dropdown {
            position: relative;
            margin-right: auto;
            
            .tag-button {
                min-width: 172px;
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 0 12px;
                border-radius: 6px !important;
                height: 32px !important;
                line-height: 32px !important;
                color: var(--black-60) !important;
                
                .icon-left {
                    width: 16px;
                    height: 16px;
                    fill: var(--black-60);
                }
                
                span {
                    flex: 1;
                    text-align: left;
                }
                
                .icon-right {
                    width: 12px;
                    height: 12px;
                    fill: var(--black-60);
                    transition: transform 0.2s;
                    
                    &.rotate {
                        transform: rotate(180deg);
                    }
                }
            }
            
            .tag-dropdown-menu {
                position: absolute;
                top: calc(100% + 4px);
                left: 0;
                min-width: 240px;
                background: white;
                border: 1px solid var(--black-20);
                border-radius: 4px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                z-index: 100;
                
                .operation-type-section {
                    padding: 12px 20px;
                    display: flex;
                    gap: 24px;
                    
                    .radio-item {
                        display: flex;
                        align-items: center;
                        cursor: pointer;
                        
                        input[type="radio"] {
                            margin: 0 8px 0 0;
                            width: 12px;
                            height: 12px;
                            accent-color: var(--dark-blue);
                        }
                        
                        span {
                            font: var(--font-fg-12);
                            color: var(--black-90);
                            user-select: none;
                        }
                    }
                }
                
                .divider-line {
                    height: 1px;
                    background-color: var(--black-20);
                    margin: 0;
                }
                
                .tag-list {
                    max-height: 300px;
                    padding: 8px 0;
                    
                    .tag-item {
                        display: flex;
                        align-items: center;
                        padding: 8px 16px;
                        cursor: pointer;
                        transition: background-color 0.2s;
                        
                        &:hover {
                            background-color: var(--black-10);
                        }
                        
                        input[type="checkbox"] {
                            margin-right: 12px;
                            cursor: pointer;
                            width: 12px;
                            height: 12px;
                            -webkit-appearance: none;
                            appearance: none;
                            border: 1px solid var(--black-50);
                            border-radius: 50%; // Make circular
                            background-color: white;
                            vertical-align: middle;
                            
                            &:checked {
                                background-color: var(--dark-blue);
                                border-color: var(--dark-blue);
                                position: relative;
                                
                                // Add checkmark - adjusted for 12px size
                                &::after {
                                    content: '✓';
                                    position: absolute;
                                    color: white;
                                    font-size: 10px;
                                    font-weight: bold;
                                    top: -1px;
                                    left: 1px;
                                }
                            }
                            
                            &:hover:not(:disabled) {
                                border-color: var(--black-75);
                            }
                            
                            &:disabled {
                                opacity: 0.5;
                                cursor: not-allowed;
                            }
                        }

                        span {
                            font: var(--font-fg-12);
                            color: var(--black-90);
                            user-select: none;
                        }

                        &.disabled {
                            opacity: 0.5;
                            cursor: not-allowed;
                            pointer-events: none;
                            
                            &:hover {
                                background-color: transparent;
                            }
                        }
                    }
                }
                
                .footer {
                    display: flex;
                    gap: 12px;
                    padding: 16px 20px;
                    background-color: transparent;
                    justify-content: flex-end;
                    
                    .clear-btn {
                        min-width: 60px;
                        height: 28px;
                        line-height: 26px;
                        border-radius: 6px;
                        font-size: 12px;
                        padding: 0 16px;
                        border: 1px solid var(--black-40);
                        background: transparent;
                        color: var(--black-80);
                        
                        &:hover {
                            background-color: var(--black-10);
                        }
                    }
                    
                    .apply-btn {
                        min-width: 80px;
                        height: 28px;
                        line-height: 26px;
                        border-radius: 6px;
                        font-size: 12px;
                        padding: 0 16px;
                        
                        &:hover {
                            background-color: var(--dark-blue-hover, #2c5aa0);
                        }
                    }
                }
            }
        }

        .right-controls {
            display: flex;
            align-items: center;
            margin-left: auto;
        }

        .ui-checkbox{
            margin: 0;
        }

        .update-time {
            color: var(--black-60);
            font: var(--font-fg-12);
            margin-right: 20px;
        }

        .btn {
            margin-left: 20px;
            display: flex;
            align-items: center;
            justify-content: center;

            svg {
                margin-right: 6px;
            }
        }

    }

    .header-divider {
        width: 100%;
        height: 2px;
        background-color: var(--black-20);
        margin-bottom: 16px;
    }

    .nav-row {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        
        .ui-navi {
            flex: 1;
        }
        
        .update-time {
            color: var(--black-60);
            font: var(--font-fg-12);
            margin-left: 20px;
            flex-shrink: 0;
        }
    }

    .ui-navi {
        width: 100%;
    }
}
</style>
